import { createContext, use } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";

import { useCurrency } from "@workspace/accounting";
import { useTenant } from "@workspace/tenant-context";

import { venueManagementApi } from "../../api";
import type { VenueDetails } from "../venue-dashboard-context";

export type VenueSettingsContextValue = ReturnType<
	typeof useVenueSettingsContext
>;

export const VenueSettingsContext = createContext<
	VenueSettingsContextValue | undefined
>(undefined);

export function useVenueSettingsContext({
	tenant,
	venue,
	refetchInterval,
}: {
	tenant: string;
	venue: VenueDetails;
	refetchInterval?: number;
}) {
	const venueId = venue.id;

	const settingsQuery = useSuspenseQuery({
		...venueManagementApi.getSettingsOptions(tenant, venueId),
		refetchInterval: refetchInterval || undefined,
	});

	const currency = useCurrency({ tenant, venueId });
	const { appInstance } = useTenant();

	const value = {
		tenant,
		appInstance,
		venueId,
		venue,
		timeZone: venue.timeZone,
		staffing: settingsQuery.data.staff,
		currency,
		paymentProvider: settingsQuery.data.paymentProvider as {
			type: string;
			accountNumber: string;
			paymentMethods: string[];
		},
	};

	return value;
}

export function useVenueSettings(): VenueSettingsContextValue {
	const ctx = use(VenueSettingsContext);
	if (!ctx) {
		throw new Error(
			"useVenueSettings must be used within a VenueSettingsProvider",
		);
	}
	return ctx;
}
